import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LIBELLES_MODE_RATTACHEMENT_CV,
  type CandidatureDetail,
  type ChampCoordonneeCv,
  type DepotCvDetail,
  type ReponseActionDepotCv,
} from '@rh/shared';
import { appel } from '../api';
import { messageCompletion } from '../envoi-cv';
import { dateHeure, nomComplet } from '../format';
import { PropositionsCoordonnees } from './CoordonneesLues';
import { DeplacerCv } from './DeplacerCv';
import { Erreur } from './Etat';

/** Nom neutre d'un CV déposé à la main : `cv-depose-AAAAMMJJ.<ext>` (apps/api/src/cv/fichier-cv.ts). */
export const NOM_CV_DEPOSE = /^cv-depose-\d{8}\.[a-z]{3,4}$/;

interface Props {
  fiche: CandidatureDetail;
  /** Change à chaque relecture de la fiche : le dépôt est relu avec elle. */
  version: number;
  onChange: () => void;
}

interface Notice {
  texte: string;
  alerte: boolean;
  candidatureId?: string;
}

/**
 * CV déposé sur la fiche (rôle rh) : téléphone et e-mail lus dans le CV à
 * ajouter d'un clic quand la fiche ne les a pas, « Déplacer vers une autre
 * candidature » et « Retirer (fichier effacé) ». Les dépôts se lisent par la
 * candidature (elle et ses origines du vivier), quel que soit leur ancienneté,
 * puis se retrouvent par l'identifiant de leur document.
 */
export function CvDeposeFiche({ fiche, version, onChange }: Props) {
  const cle = useMemo(
    () =>
      fiche.documents
        .filter((d) => d.type === 'cv' && NOM_CV_DEPOSE.test(d.nomFichier))
        .map((d) => d.id)
        .join(','),
    [fiche.documents],
  );
  const candidatureId = fiche.id;
  const [depots, setDepots] = useState<DepotCvDetail[]>([]);
  const [deplacement, setDeplacement] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!cle) {
      setDepots([]);
      return;
    }
    let vivant = true;
    const documents = new Set(cle.split(','));
    appel<DepotCvDetail[]>(`/cv-a-rattacher?vue=rattaches&candidature=${encodeURIComponent(candidatureId)}`)
      .then((liste) => vivant && setDepots(liste.filter((d) => d.document && documents.has(d.document.id))))
      .catch(() => vivant && setDepots([]));
    return () => {
      vivant = false;
    };
  }, [cle, version, candidatureId]);

  async function executer(travail: () => Promise<Notice | null>): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      setNotice(await travail());
      onChange();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Opération impossible');
    } finally {
      setEnvoi(false);
    }
  }

  function ajouter(depot: DepotCvDetail, champ: ChampCoordonneeCv, valeur: string): void {
    void executer(async () => {
      const reponse = await appel<ReponseActionDepotCv>(`/cv-a-rattacher/${depot.id}/completer`, {
        method: 'POST',
        body: JSON.stringify({ [champ]: valeur }),
      });
      return messageCompletion(reponse.completion);
    });
  }

  function retirer(depot: DepotCvDetail): void {
    if (!window.confirm('Retirer ce CV de la fiche ? Le fichier sera effacé ; les coordonnées déjà ajoutées restent.')) {
      return;
    }
    void executer(async () => {
      await appel<ReponseActionDepotCv>(`/cv-a-rattacher/${depot.id}/retirer`, { method: 'POST' });
      return { texte: 'CV retiré de la fiche : le fichier est effacé.', alerte: false };
    });
  }

  if (depots.length === 0 && !notice && !erreur) return null;

  return (
    <div className="cv-deposes-fiche">
      {notice && (
        <p className={notice.alerte ? 'notice-cv notice-cv--alerte' : 'notice-cv'} role="status">
          {notice.texte}
          {notice.candidatureId && (
            <>
              {' '}
              <Link to={`/candidatures/${notice.candidatureId}`}>Ouvrir la fiche</Link>
            </>
          )}
        </p>
      )}
      {erreur && <Erreur message={erreur} />}
      {depots.map((depot) => {
        const ici = depot.candidature?.candidatureId === fiche.id;
        return (
          <div key={depot.id} className="cv-depose">
            <p className="mention">
              {depot.mode ? LIBELLES_MODE_RATTACHEMENT_CV[depot.mode] : 'CV déposé'} le{' '}
              {dateHeure(depot.traiteLe ?? depot.deposeLe)}
              {depot.traitePar ? ` par ${depot.traitePar}` : ''}
              {!ici && ' — sur la candidature d’origine'}
            </p>
            {depot.format !== 'doc' &&
              depot.document &&
              !depot.document.aDuTexte &&
              // Le worker marque « illisible » un CV dont il n'a rien tiré : plus rien n'arrivera.
              (depot.document.lisible ? (
                <p className="mention">Lecture du CV en cours : le téléphone et l’e-mail lus s’afficheront ici.</p>
              ) : (
                <p className="mention">
                  Texte du CV illisible : aucun téléphone ni e-mail lu, complétez la fiche à la main.
                </p>
              ))}
            <PropositionsCoordonnees
              coordonnees={depot.coordonnees}
              telephoneFiche={fiche.telephone}
              emailFiche={fiche.email}
              envoi={envoi}
              onAjouter={(champ, valeur) => ajouter(depot, champ, valeur)}
            />
            {ici &&
              (deplacement === depot.id ? (
                <DeplacerCv
                  depotId={depot.id}
                  candidatureActuelle={fiche.id}
                  nomActuel={nomComplet(fiche)}
                  memePersonne={fiche.autresCandidatures}
                  onAnnuler={() => setDeplacement(null)}
                  onDeplace={(reponse, destination) => {
                    setDeplacement(null);
                    setNotice({
                      texte: `CV déplacé vers ${destination}.`,
                      alerte: false,
                      candidatureId: reponse.depot.candidature?.candidatureId,
                    });
                    onChange();
                  }}
                />
              ) : (
                <div className="actions-fiche">
                  <button
                    type="button"
                    className="bouton-second"
                    disabled={envoi}
                    onClick={() => setDeplacement(depot.id)}
                  >
                    Déplacer vers une autre candidature
                  </button>
                  <button
                    type="button"
                    className="bouton-second bouton-danger"
                    disabled={envoi}
                    onClick={() => retirer(depot)}
                  >
                    Retirer (fichier effacé)
                  </button>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
}
